// Holiday Club invoicing: data model, grouping and money maths.
//
// Pure (no Firebase imports): the teacher portal imports this file directly to show pending invoices with the
// same totals the functions will issue. Money is integer pence throughout.

export type DayChoice = "FULL" | "HALF" | "NONE";

export interface HolidayDay {
  /** The day exactly as the form shows it, e.g. "Mon 26 Oct". */
  label: string;
  /** ISO date (YYYY-MM-DD) parsed from the label, or null if it could not be parsed. */
  date: string | null;
  choice: DayChoice;
}

/** What the parent submitted. Written only by the Apps Script sync (holidayRegistrations/{responseId}.form). */
export interface HolidayRegistrationForm {
  submittedAt: string;
  childName: string;
  parentName: string;
  address: string;
  email: string;
  phone: string;
  days: HolidayDay[];
}

/** What the manager sets in the portal (holidayRegistrations/{responseId}.admin). Never touched by the sync. */
export interface HolidayRegistrationAdmin {
  cancelled?: boolean;
  discountPercentage?: number;
  discountReason?: string;
  note?: string;
}

/** holidayRegistrations/{formResponseId} */
export interface HolidayRegistration {
  id: string;
  termId: string;
  form: HolidayRegistrationForm;
  admin?: HolidayRegistrationAdmin;
  /** Set (by the functions only) once the registration is on an invoice. */
  invoiceNo?: number | null;
  /** The response was deleted from the form. Kept for history, never invoiced. */
  removedFromForm?: boolean;
}

/** holidayTerms/{formId} -- one per registration form. Written by the Apps Script sync. */
export interface HolidayTerm {
  id: string;
  name: string;
  formCreatedAt: string;
  formUrl: string;
  sheetUrl: string;
  fullDayPricePence: number;
  halfDayPricePence: number;
  days: { label: string; date: string | null }[];
  syncedAt: string;
}

export interface InvoiceChild {
  registrationId: string;
  name: string;
  days: HolidayDay[];
}

/** The billable content of an invoice, before it has a number. */
export interface InvoiceContent {
  termId: string;
  registrationIds: string[];
  email: string;
  parentName: string;
  address: string;
  children: InvoiceChild[];
  fullDays: number;
  halfDays: number;
  fullDayPricePence: number;
  halfDayPricePence: number;
  fullDaySubtotalPence: number;
  halfDaySubtotalPence: number;
  subtotalPence: number;
  discountPercentage: number;
  discountReason: string;
  discountPence: number;
  totalPence: number;
}

/** holidayInvoices/{invoiceNo} */
export interface HolidayInvoice extends InvoiceContent {
  invoiceNo: number;
  /** ISO date the invoice was (re)issued. */
  issuedDate: string;
  paid: boolean;
  paidDate: string | null;
  cancelled: boolean;
  draftId: string | null;
  draftedAt: string | null;
  draftError: string | null;
  pdfPath: string | null;
  receipt: { draftId: string; paymentDate: string; createdAt: string; pdfPath: string | null } | null;
  /** "spreadsheet": issued by the old generator and mirrored by the sync (no draft, PDF or issue date here). */
  source?: "spreadsheet";
}

export interface GroupingProblem {
  email: string;
  registrationIds: string[];
  message: string;
}

export const NO_DISCOUNT = "None";

export const normaliseEmail = (email: string) => email.trim().toLowerCase();

const discountOf = (registration: HolidayRegistration) => ({
  percentage: registration.admin?.discountPercentage ?? 0,
  reason: registration.admin?.discountReason || NO_DISCOUNT,
});

export const countDays = (days: HolidayDay[]) => ({
  fullDays: days.filter((day) => day.choice === "FULL").length,
  halfDays: days.filter((day) => day.choice === "HALF").length,
});

/**
 * Builds one invoice from registrations of the same parent. Throws if the siblings carry different discounts,
 * since one invoice shows one discount line.
 *
 * Each registration's discount is rounded to the penny on its own and then summed, which is how the previous
 * spreadsheet-based generator calculated it.
 */
export const buildInvoiceContent = (registrations: HolidayRegistration[], term: HolidayTerm): InvoiceContent => {
  if (registrations.length === 0) throw new Error("An invoice needs at least one registration.");

  const first = registrations[0];
  const discount = discountOf(first);
  for (const registration of registrations) {
    const other = discountOf(registration);
    if (other.percentage !== discount.percentage || other.reason !== discount.reason) {
      throw new Error(
        `Discount differs between ${first.form.childName} (${discount.percentage}% ${discount.reason}) ` +
        `and ${registration.form.childName} (${other.percentage}% ${other.reason}).`
      );
    }
  }
  if (!(discount.percentage >= 0 && discount.percentage <= 100)) {
    throw new Error(`Discount must be between 0 and 100%, got ${discount.percentage}.`);
  }

  let fullDays = 0;
  let halfDays = 0;
  let subtotalPence = 0;
  let discountPence = 0;
  for (const registration of registrations) {
    const counts = countDays(registration.form.days);
    const subtotal = counts.fullDays * term.fullDayPricePence + counts.halfDays * term.halfDayPricePence;
    fullDays += counts.fullDays;
    halfDays += counts.halfDays;
    subtotalPence += subtotal;
    discountPence += Math.round((subtotal * discount.percentage) / 100);
  }

  return {
    termId: term.id,
    registrationIds: registrations.map((registration) => registration.id),
    email: normaliseEmail(first.form.email),
    parentName: first.form.parentName.trim(),
    address: first.form.address.trim(),
    children: registrations.map((registration) => ({
      registrationId: registration.id,
      name: registration.form.childName.trim(),
      days: registration.form.days,
    })),
    fullDays,
    halfDays,
    fullDayPricePence: term.fullDayPricePence,
    halfDayPricePence: term.halfDayPricePence,
    fullDaySubtotalPence: fullDays * term.fullDayPricePence,
    halfDaySubtotalPence: halfDays * term.halfDayPricePence,
    subtotalPence,
    discountPercentage: discount.percentage,
    discountReason: discount.reason,
    discountPence,
    totalPence: subtotalPence - discountPence,
  };
};

/** Registrations that still need an invoice: in this term, on the form, not cancelled, not yet invoiced. */
export const isAwaitingInvoice = (registration: HolidayRegistration, termId: string) =>
  registration.termId === termId &&
  !registration.removedFromForm &&
  !registration.admin?.cancelled &&
  !registration.invoiceNo;

/**
 * Groups the term's uninvoiced registrations by parent email (siblings share one invoice). Groups that cannot be
 * invoiced (different discounts, no days booked) are reported as problems instead.
 */
export const pendingInvoices = (registrations: HolidayRegistration[], term: HolidayTerm) => {
  const byEmail = new Map<string, HolidayRegistration[]>();
  const ordered = registrations
    .filter((registration) => isAwaitingInvoice(registration, term.id))
    .sort((a, b) => a.form.submittedAt.localeCompare(b.form.submittedAt));
  for (const registration of ordered) {
    const key = normaliseEmail(registration.form.email);
    byEmail.set(key, [...(byEmail.get(key) ?? []), registration]);
  }

  const invoices: InvoiceContent[] = [];
  const problems: GroupingProblem[] = [];
  for (const [email, group] of byEmail) {
    const registrationIds = group.map((registration) => registration.id);
    try {
      const content = buildInvoiceContent(group, term);
      if (content.fullDays + content.halfDays === 0) {
        problems.push({ email, registrationIds, message: "No days booked." });
        continue;
      }
      invoices.push(content);
    } catch (error) {
      problems.push({ email, registrationIds, message: error instanceof Error ? error.message : String(error) });
    }
  }
  return { invoices, problems };
};

export const formatPounds = (pence: number) => (pence / 100).toFixed(2);

/** Price in pence from a form column label such as "Full day (£35.00)", or null if it has none. */
export const priceFromLabel = (label: string): number | null => {
  const match = /£\s*(\d+(?:\.\d{1,2})?)/.exec(label);
  return match ? Math.round(parseFloat(match[1]) * 100) : null;
};
